import { DEFAULT_CHECKIN_DATE, DEFAULT_CHECKOUT_DATE } from "@/constants";
import { DEFAULT_LOCALE, Locale, LOCALE_BASE_MAP } from "@/i18n/routing";
import triplaApi from "@/lib/triplaApi";
import {
  ERoomOrderBy,
  ERoomType,
  HotelListParams,
  IRoomListRequest,
  IRoomListResponse,
  LowestPriceHotelsResponse,
} from "@/types/roomsList";
import { IRoomTypeResponse } from "@/types/roomType";

// Get Rooms
export const getRooms = async (
  params: IRoomListRequest,
  signal?: AbortSignal,
  forceLocaleSepcificData?: boolean,
  locale: Locale = 'ja'
) => {
  try {
    const { hotel_id, room_type_codes } = params;

    const { checkinDate, checkoutDate } = validateBookingDates(new Date(params?.checkin_date), new Date(params?.checkout_date));
    const checkin_date = checkinDate?.toISOString().split("T")[0];
    const checkout_date = checkoutDate?.toISOString().split("T")[0];

    const query = {
      ...params,
      type: ERoomType.ROOMS,
      bookable: "skip",
      is_day_use: false,
      is_including_occupied: true,
      order: ERoomOrderBy.PRICE_LOW_TO_HIGH,
      mcp_currency: ERoomOrderBy.JAPAN_CURRENCY,
      "Tripla-Locale": forceLocaleSepcificData ? LOCALE_BASE_MAP[locale] : DEFAULT_LOCALE,
      room_type_codes: room_type_codes || [],
      locale: forceLocaleSepcificData ? LOCALE_BASE_MAP[locale] : DEFAULT_LOCALE,
      checkin_date,
      checkout_date,
    };
    const response = await triplaApi.get<IRoomListResponse>(
      `/hotels/${hotel_id}/rooms`,
      {
        params: { q: JSON.stringify(query) },
        signal,
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching rooms:", error);
  }
};

export const getRoomsForHotelPetLanding = async (
  params: IRoomListRequest,
  signal?: AbortSignal,
) => {
  try {
    const { hotel_id, rooms, checkin_date, checkout_date } = params;

    const query = {
      type: ERoomType.ROOMS,
      rooms: rooms,
      checkin_date,
      checkout_date,
    };

    const response = await triplaApi.get<IRoomListResponse>(
      `/hotels/${hotel_id}/rooms`,
      {
        params: { type: "plants", q: JSON.stringify(query) },
        signal,
      },
    );

    return response.data;
  } catch (error) {
    console.error("Error fetching rooms:", error);
    throw error;
  }
};

// Get Room Types
export const getRoomTypes = async (hotelId: string) => {
  try {
    const response = await triplaApi.get<IRoomTypeResponse>(
      `/hotels/${hotelId}/room_types`,
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching room types:", error);
  }
};

export const getLowestPriceHotels = async (params: HotelListParams) => {
  try {
    const {
      brandId,
      page = 1,
      per_page = 5,
      rooms,
      hotel_codes,
      is_including_lowest_price = true,
      is_including_max_of_lowest_price_next_30_days = false,
    } = params;
    const queryObject = {
      ...params,
      page,
      per_page,
      rooms,
      hotel_codes,
    };
    const response = await triplaApi.get<LowestPriceHotelsResponse>(
      `/hotel_brands/${brandId}/hotels`,
      {
        params: {
          is_including_max_of_lowest_price_next_30_days,
          is_including_lowest_price,
          q: JSON.stringify(queryObject),
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error("Error fetching hotel list:", error);
    throw error;
  }
};



function validateBookingDates(checkinDate: Date, checkoutDate: Date) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  if (
    checkinDate < today ||
    checkoutDate < today ||
    checkoutDate < checkinDate
  ) {
    return {
      checkinDate: new Date(DEFAULT_CHECKIN_DATE),
      checkoutDate: new Date(DEFAULT_CHECKOUT_DATE),
    };
  }

  return {
    checkinDate,
    checkoutDate,
  };
}